// VacancyLinksSection — posted vacancy links, each with an id, each blockable on its own.
//
// OWNER, 2026-09-16: "sometimes when I post links in different groups someone may take it and
// attack us, so we should be able to block each link separately. So every link must have IDs."
// And, pointing at the Add-candidate modal: "here we need create link for which platform —
// choose — and comment on link to remember why it was created, so later they can block it."
//
// Two components on purpose. `VacancyLinksPanel` is pure — it renders what it is given and
// reports what was pressed — so the harness can draw every state outside the login gate, which
// is the only way this surface can be seen before an agent sees it. `VacancyLinksSection` is the
// thin container that loads and writes.
//
// ⚠️ THE COMMENT IS REQUIRED, and that is the feature, not validation fussiness. A link with
// only a platform is "the Facebook one" — and there will be ten of those. The day one of them is
// abused, nobody can tell which to block without killing the other nine, which is the exact
// failure the owner described. So the button stays disabled until the link says WHERE it went.
//
// ⚠️ BLOCKING ONE LINK IS NOT A LOCK WHILE LINKS WITHOUT AN ID ARE ACCEPTED: an attacker deletes
// `?l=…` from the URL and submits the bare form. The admin's switch at the bottom closes that
// door (config `vacancy_link_policy.require_id`), and it is off until the owner turns it on,
// because the id-less links posted on 2026-09-10 are live on boards and must be reposted first.
import { useEffect, useState } from 'react'
import { t, getLanguage } from '../lib/i18n.js'
import { Icon, IconText } from './Icon.jsx'
import { card, input, btn } from './desk/primitives.jsx'
import {
  listVacancyLinks, countApplicationsByVacancyLink, createVacancyLink, setVacancyLinkBlocked,
  getVacancyLinkPolicy, setConfigKey, listVacancyLinkManagers,
} from '../db/index.js'

// The platforms a link can be posted on. The server normalises whatever it is given through the
// same resolver the id-less links used, so these labels are exactly what lands in
// candidates.source — and the Candidates tab's source filter groups them with everything else.
export const VACANCY_PLATFORMS = ['Facebook', 'Instagram', 'TikTok', 'Awork', 'Jobify', 'jobs.ge', 'hr.ge', 'LinkedIn', 'Telegram', 'Other']

// The vacancy lives on sentavus.com (owner 2026-09-19): posted links open the public job page, whose form sends the
// same ?l= to the same API. Old /vacancy links still work — vercel.json redirects them there with their query.
export const VACANCY_PAGE = 'https://sentavus.com/ka/jobs/real-estate-agent-tbilisi'
export const vacancyLinkUrl = id => `${VACANCY_PAGE}?l=${id}`

// ⚠️ A MONTH TABLE, NOT toLocaleDateString('ka-GE') — measured in this app's browser 2026-09-16:
// 'ka-GE' returns "Sep 15" (no Georgian date data in that Chromium build), which drops an English
// month into a Georgian sentence ("ბლოკირებულია Sep 15-დან"). The vacancy page hit the same wall.
const KA_MONTHS = ['იან', 'თებ', 'მარ', 'აპრ', 'მაი', 'ივნ', 'ივლ', 'აგვ', 'სექ', 'ოქტ', 'ნოე', 'დეკ']
const EN_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtDate = iso => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return getLanguage() === 'ka' ? `${d.getDate()} ${KA_MONTHS[d.getMonth()]}.` : `${d.getDate()} ${EN_MONTHS[d.getMonth()]}`
}

export function VacancyLinksPanel({
  links = null, counts = {}, created = null, busy = false, err = null,
  showCreator = false, policy = null, canSetPolicy = false,
  managers = null,
  onCreate, onBlock, onUnblock, onPolicy,
}) {
  const [platform, setPlatform] = useState('')
  // Owner 2026-09-16: "let admin also specify manager. if no manager selected then all managers
  // allowed as it is now." Empty = unassigned, the default, so not choosing changes nothing.
  const [managerId, setManagerId] = useState('')
  const [comment, setComment] = useState('')
  const [copied, setCopied] = useState(null)
  const ready = platform && comment.trim().length > 0

  async function copy(id) {
    try { await navigator.clipboard.writeText(vacancyLinkUrl(id)); setCopied(id); setTimeout(() => setCopied(null), 1500) }
    catch { /* clipboard blocked — the url is on screen to select by hand */ }
  }

  return (
    <div className={card}>
      <p className="mb-1 text-xs font-semibold text-muted-foreground"><Icon name="link" /> {t('vlink_title')}</p>
      <p className="mb-3 text-xs text-muted-foreground">{t('vlink_sub')}</p>

      {/* ── create ─────────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {VACANCY_PLATFORMS.map(p => (
            <button key={p} type="button" onClick={() => setPlatform(platform === p ? '' : p)}
              className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${platform === p
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:border-primary/40'}`}>
              {p === 'Other' ? t('vlink_platform_other') : p}
            </button>
          ))}
        </div>
        {Array.isArray(managers) && managers.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-muted-foreground">{t('vlink_manager')}</span>
            {[{ id: '', name: t('vlink_manager_any') }, ...managers].map(m => (
              <button key={m.id || 'any'} type="button" onClick={() => setManagerId(m.id)}
                className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors ${managerId === m.id
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                {m.name}
              </button>
            ))}
          </div>
        )}
        <input className={input} value={comment} maxLength={300}
          onChange={e => setComment(e.target.value)}
          placeholder={t('vlink_comment_ph')} />
        <button type="button" className={btn('primary')} disabled={!ready || busy}
          onClick={async () => {
            const manager = (managers || []).find(m => m.id === managerId) || null
            const ok = await onCreate?.({ platform, comment: comment.trim(), manager })
            if (ok) { setComment(''); setPlatform(''); setManagerId('') }
          }}>
          {busy ? t('vlink_creating') : t('vlink_create')}
        </button>
        {!ready && <p className="text-[11px] text-muted-foreground/70">{t('vlink_need_both')}</p>}
      </div>

      {created && (
        <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-2.5">
          <p className="break-all font-mono text-xs text-foreground">{vacancyLinkUrl(created.id)}</p>
          <button type="button" className={`${btn('ghost')} mt-2`} onClick={() => copy(created.id)}>
            {copied === created.id ? t('vlink_copied') : <IconText>{`📋 ${t('vlink_copy')}`}</IconText>}
          </button>
        </div>
      )}
      {err && <p className="mt-2 text-sm text-destructive">{err}</p>}

      {/* ── the links ──────────────────────────────────────────────────────── */}
      <p className="mt-4 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/60">{t('vlink_list')}</p>
      {links === null ? <p className="text-xs text-muted-foreground">…</p>
        : !links.length ? <p className="text-xs text-muted-foreground">{t('vlink_none')}</p>
        : (
          <div className="max-h-72 space-y-1.5 overflow-y-auto pr-0.5">
            {links.map(l => {
              const blocked = !!l.blocked_at
              const n = counts[l.id] || 0
              return (
                <div key={l.id} className={`rounded-lg border px-2.5 py-2 text-xs ${blocked ? 'border-destructive/40 bg-destructive/5' : 'border-border'}`}>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      {/* The COMMENT leads — it is what tells ten Facebook links apart. */}
                      <p className={`font-medium ${blocked ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{l.comment}</p>
                      <p className="mt-0.5 text-muted-foreground">
                        {[l.platform === 'Other' ? t('vlink_platform_other') : l.platform, fmtDate(l.created_at),
                          showCreator ? l.created_by_name : null,
                          l.manager_name ? `→ ${l.manager_name}` : null,
                          t('vlink_applications').replace('{n}', n), `id ${l.id}`].filter(Boolean).join(' · ')}
                      </p>
                      {blocked && (
                        <p className="mt-0.5 text-destructive">
                          {t('vlink_blocked_since').replace('{when}', fmtDate(l.blocked_at))}
                          {l.blocked_by_name ? ` · ${l.blocked_by_name}` : ''}
                          {l.block_reason ? ` — ${l.block_reason}` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      {!blocked && (
                        <button type="button" title={t('vlink_copy')} onClick={() => copy(l.id)}
                          className="rounded border border-border px-1.5 py-0.5 hover:bg-accent">
                          {copied === l.id ? '✓' : <Icon name="clipboard" />}
                        </button>
                      )}
                      {blocked ? (
                        <button type="button" onClick={() => onUnblock?.(l)}
                          className="rounded border border-border px-2 py-0.5 font-medium hover:bg-accent">
                          {t('vlink_unblock')}
                        </button>
                      ) : (
                        <button type="button" onClick={() => onBlock?.(l)}
                          className="rounded border border-destructive/50 px-2 py-0.5 font-medium text-destructive hover:bg-destructive/10">
                          {t('vlink_block')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

      {/* ── the switch that makes a block a lock (admin) ───────────────────── */}
      {canSetPolicy && policy !== null && (
        <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border border-border p-2.5 text-xs">
          <input type="checkbox" className="mt-0.5" checked={!!policy.require_id}
            onChange={e => onPolicy?.({ require_id: e.target.checked })} />
          <span>
            <span className="font-medium text-foreground">{t('vlink_require_id')}</span>
            <span className="mt-0.5 block text-muted-foreground">{t('vlink_require_id_sub')}</span>
          </span>
        </label>
      )}
    </div>
  )
}

export function VacancyLinksSection({ me = null, adminName = null, showAll = false, canSetPolicy = false }) {
  const [links, setLinks] = useState(null)
  const [counts, setCounts] = useState({})
  const [created, setCreated] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [policy, setPolicy] = useState(null)
  const [managers, setManagers] = useState(null)

  // A manager sees the links THEY made; an admin or general manager sees every link, because
  // the person blocking an attack is not necessarily the person who posted the link.
  const scopeId = showAll ? null : (me?.id || null)

  async function load() {
    try {
      const rows = await listVacancyLinks({ agentId: scopeId })
      setLinks(rows)
      setCounts(await countApplicationsByVacancyLink(rows.map(r => r.id)).catch(() => ({})))
    } catch (e) { setLinks([]); setErr(e.message) }
  }
  useEffect(() => { load() }, [scopeId])
  useEffect(() => {
    if (!canSetPolicy) return
    // A policy we could not read renders NO switch rather than an unchecked one — see the helper.
    getVacancyLinkPolicy().then(setPolicy).catch(() => setPolicy(null))
  }, [canSetPolicy])
  useEffect(() => {
    // Only management names a manager; a manager's own link is already theirs by who made it.
    if (!showAll) return
    listVacancyLinkManagers().then(setManagers).catch(() => setManagers([]))
  }, [showAll])

  return (
    <VacancyLinksPanel links={links} counts={counts} created={created} busy={busy} err={err}
      showCreator={showAll} policy={policy} canSetPolicy={canSetPolicy} managers={managers}
      onCreate={async ({ platform, comment, manager }) => {
        setBusy(true); setErr(null)
        try {
          const row = await createVacancyLink({ platform, comment, me, adminName, manager })
          setCreated(row); await load(); return true
        } catch (e) { setErr(e.message); return false }
        finally { setBusy(false) }
      }}
      onBlock={async l => {
        // A reason is asked for but not required: in the middle of an attack, "block now" must be
        // one decision, and the comment already says which posting this was.
        const reason = window.prompt(t('vlink_block_confirm').replace('{comment}', l.comment), '')
        if (reason === null) return
        try { await setVacancyLinkBlocked(l.id, true, { byName: me?.name || adminName, reason: reason.trim() || null }); await load() }
        catch (e) { setErr(e.message) }
      }}
      onUnblock={async l => {
        if (!window.confirm(t('vlink_unblock_confirm').replace('{comment}', l.comment))) return
        try { await setVacancyLinkBlocked(l.id, false); await load() }
        catch (e) { setErr(e.message) }
      }}
      onPolicy={async next => {
        if (next.require_id && !window.confirm(t('vlink_require_id_confirm'))) return
        try {
          // A JSON object in `values`, merged by the key's own rule — never a bare boolean,
          // which the server's vocabulary loader would ignore.
          await setConfigKey('vacancy_link_policy', { ...(policy || {}), ...next })
          setPolicy(p => ({ ...(p || {}), ...next }))
        } catch (e) { setErr(e.message) }
      }} />
  )
}
